/**
 * Date utilities for handling timezone-specific operations
 * All dates in the system use America/Sao_Paulo timezone
 */

export const TIMEZONE = "America/Sao_Paulo";

/**
 * Parse a time string (HH:MM) into hour and minute
 */
export const parseTime = (timeString: string): { hour: number; minute: number } => {
  if (!timeString) return { hour: 0, minute: 0 };

  // Extract time from HH:MM or HH:MM:SS format
  const timeMatch = timeString.match(/^(\d{2}):(\d{2})/);
  if (timeMatch) {
    return {
      hour: parseInt(timeMatch[1], 10),
      minute: parseInt(timeMatch[2], 10),
    };
  }

  return { hour: 0, minute: 0 };
};

/**
 * Format a time string (HH:MM) for display
 */
export const formatTime = (timeString: string): string => {
  if (!timeString) return "";

  // If it's already in HH:MM format, return as is
  if (timeString.match(/^\d{2}:\d{2}$/) || timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return timeString.slice(0, 5);
  }

  // If it's a full datetime string, extract just the time
  try {
    const date = new Date(timeString);
    return date.toLocaleTimeString("pt-BR", {
      timeZone: TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return timeString;
  }
};

export const formatDateForDisplay = (dateString: string): string => {
  // Handle YYYY-MM-DD format by adding time to avoid timezone issues
  const date = new Date(dateString + "T12:00:00");
  return date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Format a date string for display (short format) using Brazil timezone
 */
export const formatDateShort = (dateString: string): string => {
  // Handle YYYY-MM-DD format by adding time to avoid timezone issues
  const date = new Date(dateString + "T12:00:00");
  return date.toLocaleDateString("pt-BR", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Get today's date in YYYY-MM-DD format using Brazil timezone
 */
export const getTodayInBrazil = (): string => {
  return createDateInBrazil(new Date().toISOString().split('T')[0]).toISOString().split('T')[0];
};

/**
 * First and last calendar day of the current month in {@link TIMEZONE}, as YYYY-MM-DD (API / inputs).
 */
export const getBrazilCalendarMonthRange = (): { from: string; to: string } => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  if (!year || !month) {
    const ymd = getTodayInBrazil();
    return { from: `${ymd.slice(0, 7)}-01`, to: ymd };
  }
  const yearNum = Number(year);
  /** 1–12 (Janeiro = 1) */
  const monthNum = Number(month);
  const from = `${year}-${month}-01`;
  const lastDay = new Date(yearNum, monthNum, 0).getDate();
  const to = `${year}-${month}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
};

/**
 * Format time for display using Brazil timezone
 */
export const formatTimeForDisplay = (timeString: string): string => {
  return timeString; // Time slots are already in local format (HH:MM)
};

/**
 * Get current time in Brazil timezone
 */
export const getCurrentTimeInBrazil = (): Date => {
  const now = new Date();
  return createDateInBrazil(now.toISOString().split('T')[0]);
};

/**
 * Check if a date is valid and not in the past (Brazil timezone)
 */
export const isValidFutureDate = (dateString: string): boolean => {
  const inputDate = createDateInBrazil(dateString);
  const today = getTodayInBrazil();
  const todayDate = createDateInBrazil(today);

  return inputDate.getTime() >= todayDate.getTime();
};

/**
 * Convert a date to Brazil timezone and format for API
 */
export const formatDateForAPI = (dateString: string): string => {
  return dateString; // Keep ISO format for API
};

/**
 * Get localized date/time string for Brazil
 */
export const getLocalizedDateTime = (date: Date): string => {
  return date.toLocaleString("pt-BR", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format a date/time string for display using Brazil locale.
 * Accepts ISO strings (e.g. 2025-02-24T15:30:00.000Z) or date-only (YYYY-MM-DD).
 */
export const formatDateTime = (dateString: string | null): string => {
  if (!dateString || !dateString.trim()) return "—";
  try {
    const date = new Date(dateString.trim());
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("pt-BR", {
      timeZone: TIMEZONE,
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
};

/**
 * Check if a birthday (date string) matches today's date (month and day)
 * @param birthday - Date string in YYYY-MM-DD format
 * @returns true if the birthday month and day match today's month and day
 */
export const isBirthdayToday = (birthday?: string): boolean => {
  if (!birthday) return false;
  const today = createDateInBrazil(getTodayInBrazil());
  const birthdayDate = createDateInBrazil(birthday);
  return (
    today.getMonth() === birthdayDate.getMonth() &&
    today.getDate() === birthdayDate.getDate()
  );
};


/**
 * Create a Date object for a given date string in Brazil timezone
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Date object representing the date at midnight in Brazil timezone
 */
export const createDateInBrazil = (dateString: string): Date => {
  const brazilDate = new Date(`${dateString}T12:00:00`).toLocaleDateString('en-CA', {
    timeZone: TIMEZONE
  });

  const date = new Date(`${brazilDate}T00:00:00`);
  return date;
};

/**
 * Apply DD/MM/YYYY mask to birthday input
 * @param value - Raw input value
 * @returns Formatted string in DD/MM/YYYY format
 */
export const applyBirthdayMask = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, "");

  // Apply mask based on length
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  }
};

/**
 * Validate birthday format and value
 * @param value - Date string in DD/MM/YYYY format
 * @returns Validation result with isValid flag and optional error message
 */
export const validateBirthday = (value: string): { isValid: boolean; error?: string } => {
  if (!value.trim()) {
    return { isValid: false, error: "Data de nascimento é obrigatória" };
  }

  // Check format DD/MM/YYYY
  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = value.match(dateRegex);

  if (!match) {
    return { isValid: false, error: "Formato inválido. Use DD/MM/AAAA" };
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Validate day
  if (day < 1 || day > 31) {
    return { isValid: false, error: "Dia inválido" };
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, error: "Mês inválido" };
  }

  // Validate year (reasonable range)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return { isValid: false, error: "Ano inválido" };
  }

  // Check if date is valid (e.g., not 31/02)
  const date = new Date(year, month - 1, day);
  if (
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return { isValid: false, error: "Data inválida" };
  }

  // Check if date is not in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) {
    return { isValid: false, error: "Data de nascimento não pode ser no futuro" };
  }

  return { isValid: true };
};

/**
 * Convert DD/MM/YYYY format to YYYY-MM-DD format for API
 * @param value - Date string in DD/MM/YYYY format
 * @returns Date string in YYYY-MM-DD format
 */
export const convertBirthdayToAPIFormat = (value: string): string => {
  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = value.match(dateRegex);
  if (match) {
    const day = match[1];
    const month = match[2];
    const year = match[3];
    return `${year}-${month}-${day}`;
  }
  return value;
};
